import React, { useState } from "react";
import { Button, Tooltip } from "antd";
import { FileDoneOutlined, TranslationOutlined } from "@ant-design/icons";
import { map } from "lodash";
import { EComModal } from "components/EComModal";
import { EMPTY } from "constants/common";
import { AiccSandboxFeedbackResult, AiccSandboxWordsResult } from "types/model/aiccPromptSandbox";
import {
  ACTION_NO_ANSWER,
  SUPPORT_ACHIEVEMENTS,
  SUPPORT_FEEDBACK,
  SUPPORT_FEEDBACK_DESCRIPTION,
  SUPPORT_FEEDBACK_EMPTY,
  SUPPORT_NEEDS_TURNS,
  SUPPORT_IDENTIFY,
  SUPPORT_SUGGESTIONS,
  SUPPORT_SUMMARISE,
  SUPPORT_WORDS,
  SUPPORT_WORDS_DESCRIPTION,
  SUPPORT_WORDS_EMPTY,
  SUPPORT_WORDS_MEANS,
  SUPPORT_WORDS_WORD,
} from "../configs";
import styles from "../Sandbox.module.scss";

interface Props {
  hasConversation: boolean;
  feedback?: AiccSandboxFeedbackResult | null;
  isSummarising: boolean;
  onSummarise: () => void;
  words?: AiccSandboxWordsResult | null;
  isIdentifying: boolean;
  onIdentify: () => void;
}

const MODAL_WIDTH = 640;

function FeedbackResult({ feedback }: { feedback: AiccSandboxFeedbackResult }) {
  const { summary, achievements, suggestions } = feedback;

  if (!summary && !achievements.length && !suggestions.length) {
    return <div className={styles.supportEmpty}>{SUPPORT_FEEDBACK_EMPTY}</div>;
  }

  return (
    <div className={styles.supportResult}>
      {!!summary && <div>{summary}</div>}
      {map(
        [
          { label: SUPPORT_ACHIEVEMENTS, items: achievements },
          { label: SUPPORT_SUGGESTIONS, items: suggestions },
        ],
        ({ label, items }) =>
          !!items.length && (
            <div key={label}>
              <div className={styles.supportLabel}>{label}</div>
              <ul className={styles.supportList}>
                {map(items, (item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
          )
      )}
    </div>
  );
}

function WordsResult({ words }: { words: AiccSandboxWordsResult }) {
  if (!words.words.length) {
    return <div className={styles.supportEmpty}>{SUPPORT_WORDS_EMPTY}</div>;
  }

  return (
    <div className={styles.wordTable}>
      <div className={styles.wordHead}>{SUPPORT_WORDS_WORD}</div>
      <div className={styles.wordHead}>{SUPPORT_WORDS_MEANS}</div>
      {map(words.words, ({ sourceText, targetText }, index) => (
        <React.Fragment key={index}>
          <div className={styles.wordSource}>{sourceText}</div>
          <div className={styles.wordTarget}>{targetText || EMPTY}</div>
        </React.Fragment>
      ))}
    </div>
  );
}

function SupportingFunctions({
  hasConversation,
  feedback,
  isSummarising,
  onSummarise,
  words,
  isIdentifying,
  onIdentify,
}: Props) {
  const [openPanel, setOpenPanel] = useState<"words" | "feedback">();

  const trigger = (panel: "words" | "feedback", icon: JSX.Element, text: string) => (
    <Tooltip title={hasConversation ? "" : SUPPORT_NEEDS_TURNS}>
      <span>
        <Button type="text" size="small" icon={icon} disabled={!hasConversation} onClick={() => setOpenPanel(panel)}>
          {text}
        </Button>
      </span>
    </Tooltip>
  );

  return (
    <div className={styles.supportRow}>
      {trigger("words", <TranslationOutlined />, SUPPORT_WORDS)}
      {trigger("feedback", <FileDoneOutlined />, SUPPORT_FEEDBACK)}

      <EComModal
        visible={openPanel === "words"}
        title={SUPPORT_WORDS}
        width={MODAL_WIDTH}
        footer={null}
        onCancel={() => setOpenPanel(undefined)}
      >
        <div className={styles.supportPanel}>
          <div className={styles.supportDescription}>{SUPPORT_WORDS_DESCRIPTION}</div>
          <div>
            <Button type="primary" shape="round" loading={isIdentifying} onClick={onIdentify}>
              {SUPPORT_IDENTIFY}
            </Button>
          </div>
          {words !== undefined &&
            (words ? <WordsResult words={words} /> : <div className={styles.supportEmpty}>{ACTION_NO_ANSWER}</div>)}
        </div>
      </EComModal>

      <EComModal
        visible={openPanel === "feedback"}
        title={SUPPORT_FEEDBACK}
        width={MODAL_WIDTH}
        footer={null}
        onCancel={() => setOpenPanel(undefined)}
      >
        <div className={styles.supportPanel}>
          <div className={styles.supportDescription}>{SUPPORT_FEEDBACK_DESCRIPTION}</div>
          <div>
            <Button type="primary" shape="round" loading={isSummarising} onClick={onSummarise}>
              {SUPPORT_SUMMARISE}
            </Button>
          </div>
          {feedback !== undefined &&
            (feedback ? (
              <FeedbackResult feedback={feedback} />
            ) : (
              <div className={styles.supportEmpty}>{ACTION_NO_ANSWER}</div>
            ))}
        </div>
      </EComModal>
    </div>
  );
}

export default SupportingFunctions;
